import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { AnalyticsNote, ExpandableDisclaimer } from "@/components/Compliance";
import usePageMeta from "@/hooks/usePageMeta";

export default function ProductPageLayout({
  meta,
  overline,
  title,
  accent,
  lede,
  capabilities = [],
  approach = [],
  notWhatWeDo = [],
}) {
  usePageMeta(meta);

  return (
    <div className="min-h-screen bg-surface-base text-white">
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0 grid-bg grid-radial-mask" />
        <div className="absolute inset-0 spotlight-column opacity-70" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 pt-36 md:pt-44 pb-20 md:pb-24">
          <div className="overline mb-6">{overline}</div>
          <h1 className="font-display font-medium text-[38px] sm:text-5xl lg:text-[62px] leading-[1.04] tracking-tighter max-w-4xl">
            {title} <span className="brand-text-gradient">{accent}</span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-zinc-400 max-w-3xl leading-relaxed">{lede}</p>
          <div className="mt-10 max-w-2xl">
            <AnalyticsNote />
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="relative bg-surface-base border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="label-xs mb-8">Capabilities</div>
          <div className="grid md:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden">
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={c.t} data-testid={`capability-${i}`} className="trace-border rounded-none p-8">
                  <div className="flex items-start gap-5">
                    <div className="shrink-0 w-11 h-11 rounded-md border border-white/10 bg-white/[0.03] grid place-items-center">
                      <Icon className="w-5 h-5 text-ai-accent" />
                    </div>
                    <div>
                      <div className="font-mono text-[11px] text-zinc-500 tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</div>
                      <h3 className="mt-1 font-display font-medium text-xl tracking-tight">{c.t}</h3>
                      <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{c.d}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      {approach.length > 0 && (
        <section className="relative bg-surface-base border-b border-white/[0.06]">
          <div className="absolute inset-0 grid-bg-fine opacity-50 grid-radial-mask" />
          <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
            <div className="label-xs mb-8">How it works</div>
            <div className="grid gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden">
              {approach.map((a, i) => (
                <div key={a.t} data-testid={`approach-${i}`} className="bg-[#0B0C10] px-6 md:px-10 py-7 grid md:grid-cols-12 gap-4 items-baseline">
                  <div className="md:col-span-1 font-mono text-sm text-ai-accent">{String(i + 1).padStart(2, "0")}</div>
                  <div className="md:col-span-4 font-display font-medium text-lg tracking-tight">{a.t}</div>
                  <div className="md:col-span-7 text-sm text-zinc-400 leading-relaxed">{a.d}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHAT THIS IS NOT */}
      {notWhatWeDo.length > 0 && (
        <section className="bg-surface-base border-b border-white/[0.06]">
          <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <div className="label-xs mb-5">Scope of the platform</div>
                <h2 className="font-display font-medium text-2xl md:text-3xl tracking-tight leading-snug">
                  What this is <span className="text-zinc-500">not.</span>
                </h2>
                <p className="mt-5 text-sm text-zinc-400 leading-relaxed">
                  We are deliberately explicit about the boundaries of what Adviserz provides. The platform is analytical
                  technology — it does not provide investment advice or personalised recommendations.
                </p>
              </div>
              <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
                {notWhatWeDo.map((n) => (
                  <li key={n} className="rounded-md border border-white/[0.08] bg-white/[0.015] px-4 py-3.5 text-sm text-zinc-400">
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative bg-surface-base overflow-hidden">
        <div className="absolute inset-0 ambient-glow" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <h2 className="font-display font-medium text-3xl md:text-4xl tracking-tight leading-tight">
            Platform access is <span className="text-zinc-500">in development.</span>
          </h2>
          <p className="mt-5 text-zinc-400 max-w-xl mx-auto">
            Register your interest to be notified when early access opens. No payment is collected.
          </p>
          <Link to="/early-access" data-testid="product-cta"
            className="mt-9 inline-flex items-center gap-2 px-6 py-3.5 rounded-md brand-gradient text-white font-medium hover:brightness-110 transition-all">
            Join Early Access <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="mt-12 text-left">
            <ExpandableDisclaimer />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
